"use client"

import { useState } from "react"
import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  FileText,
  HelpCircle,
  Layers,
  Mic2,
  Scissors,
  Settings2,
  Upload,
  Wand2,
} from "lucide-react"
import { cn } from "@/lib/utils"

/* ── types ── */
interface GuideItem {
  icon: React.ElementType
  title: string
  description: string
  steps: string[]
}

interface FaqItem {
  question: string
  answer: string
}

/* ── data ── */
const GUIDES: GuideItem[] = [
  {
    icon: Upload,
    title: "Getting Started — Upload a File",
    description: "How to import your source video or audio into Neo Dub Studio.",
    steps: [
      "Open the Генерація репліка tab.",
      "In the Original Audio section click 'Аудіо або відео файл' or drag and drop your .mp4 / .wav file.",
      "The system extracts the audio track and displays the waveform automatically.",
      "Verify the file name and duration shown in the player.",
    ],
  },
  {
    icon: Layers,
    title: "Working with Dubbing Blocks",
    description: "Each block represents one dialogue segment. You can add, split, merge, and reorder them.",
    steps: [
      "Blocks are created automatically during transcription.",
      "Click a block to select it and edit the prompt text.",
      "Use Split to divide a block at the playhead position.",
      "Use Merge to combine adjacent blocks into one.",
      "Drag the handles on the waveform to adjust block boundaries.",
    ],
  },
  {
    icon: Mic2,
    title: "Choosing a Voice",
    description: "Each block can use a different voice, or inherit the global default.",
    steps: [
      "Open Global Settings via the button in the left panel.",
      "Select a default voice that applies to all blocks.",
      "To override per block, expand the block and choose a voice from the dropdown.",
      "Click the preview icon to audition the voice before generating.",
    ],
  },
  {
    icon: Wand2,
    title: "Generating Audio",
    description: "Run AI synthesis on one block or all blocks at once.",
    steps: [
      "To generate a single block press the Generate button inside it.",
      "To generate all blocks at once use the 'Generate all' button at the top right of the blocks list.",
      "Monitor progress via the status indicator on each block.",
      "Once ready, the waveform turns green and you can preview the result.",
    ],
  },
  {
    icon: Scissors,
    title: "Edit Audio — Sync & Stretch",
    description: "Fine-tune timing with the Edit Audio panel.",
    steps: [
      "Click the Edit Audio button inside a generated block's Actions section.",
      "The panel unfolds below the block showing the original (red) and generated (green) waveforms.",
      "Drag the generated waveform to shift its position in time.",
      "Enable Stretch mode to lengthen or shorten the generated clip to fit the slot.",
      "Click Save Synchronization to apply the changes.",
    ],
  },
  {
    icon: Settings2,
    title: "Global Settings",
    description: "Configure voice, environment, and export options that apply to the entire project.",
    steps: [
      "Click 'Open global settings' in the left panel of the Генерація репліка tab.",
      "Set the target language for dubbing.",
      "Choose the default voice and environment preset.",
      "Adjust pace and seed for consistent generation.",
      "Changes apply to all new generations; existing blocks are not re-generated automatically.",
    ],
  },
]

const FAQS: FaqItem[] = [
  {
    question: "What file formats are supported?",
    answer: "Neo Dub Studio accepts .mp4, .mov, .mkv video files and .wav, .mp3, .flac audio files. The system automatically extracts the audio track from video files.",
  },
  {
    question: "How long does generation take?",
    answer: "A single block of 10–25 seconds typically generates in 5–15 seconds depending on server load. Generating all blocks in parallel is faster than one by one.",
  },
  {
    question: "Can I use my own voice clone?",
    answer: "Yes. Custom voice clones can be uploaded in Account > Voices. Once uploaded they appear in the voice selector on each block and in Global Settings.",
  },
  {
    question: "What does the Seed value control?",
    answer: "The seed locks the randomness of the synthesis. Using the same seed and prompt twice produces nearly identical audio. Change the seed to get a different performance from the model.",
  },
  {
    question: "What is Smart Overlay / Stretch mode?",
    answer: "Stretch mode time-scales the generated audio to fit exactly within the original timecode slot without changing pitch. Use it when the dubbed line is slightly longer or shorter than the original.",
  },
  {
    question: "How do I export the final mix?",
    answer: "Go to the Злиття та експорт tab. Choose whether to export audio-only or merged video, select the output format, then click Export. The file downloads to your device.",
  },
  {
    question: "Is my source video stored on your servers?",
    answer: "Files are uploaded to temporary secure storage for the duration of your session. They are deleted automatically after 24 hours unless you save the project to your account.",
  },
  {
    question: "Which languages are supported?",
    answer: "The transcription engine supports 50+ languages. Voice synthesis is currently available in English, Spanish (LatAm), Ukrainian, German, French, Italian, Portuguese, and Japanese. More are added regularly.",
  },
]

/* ── sub-components ── */
function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <h3 className="mb-5 text-sm font-semibold uppercase tracking-wider text-muted-foreground">{title}</h3>
      {children}
    </div>
  )
}

function GuideCard({ guide }: { guide: GuideItem }) {
  const [open, setOpen] = useState(false)
  const Icon = guide.icon
  return (
    <div className="rounded-lg border border-border/60 bg-background transition-colors hover:border-border">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-4 px-5 py-4 text-left"
      >
        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
          <Icon className="size-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground">{guide.title}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{guide.description}</p>
        </div>
        <ChevronDown
          className={cn("size-4 shrink-0 text-muted-foreground transition-transform", open && "rotate-180")}
        />
      </button>
      {open && (
        <div className="border-t border-border/60 px-5 py-4">
          <ol className="space-y-2">
            {guide.steps.map((step, i) => (
              <li key={i} className="flex gap-3 text-sm text-muted-foreground">
                <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[10px] font-bold text-primary">
                  {i + 1}
                </span>
                {step}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  )
}

function FaqRow({ item }: { item: FaqItem }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border-b border-border/60 last:border-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-4 py-4 text-left"
      >
        <span className="text-sm font-medium text-foreground">{item.question}</span>
        <ChevronRight
          className={cn("size-4 shrink-0 text-muted-foreground transition-transform", open && "rotate-90")}
        />
      </button>
      {open && (
        <p className="pb-4 text-sm leading-relaxed text-muted-foreground">{item.answer}</p>
      )}
    </div>
  )
}

/* ── main ── */
export function DocsPage() {
  const [tab, setTab] = useState<"guides" | "faq">("guides")

  return (
    <div className="flex-1 overflow-auto">
      <div className="mx-auto w-full max-w-7xl space-y-6 p-6">

        {/* Page header */}
        <div className="flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-xl bg-primary/15 text-primary">
            <BookOpen className="size-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold">Documentation</h1>
            <p className="text-sm text-muted-foreground">Guides, manuals and frequently asked questions</p>
          </div>
        </div>

        {/* Tab toggle */}
        <div className="flex gap-1 rounded-lg border border-border bg-secondary/30 p-1 w-fit">
          {(["guides", "faq"] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={cn(
                "flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors",
                tab === t
                  ? "bg-card text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {t === "guides" ? <FileText className="size-4" /> : <HelpCircle className="size-4" />}
              {t === "guides" ? "Manuals & Guides" : "FAQ"}
            </button>
          ))}
        </div>

        {/* Guides */}
        {tab === "guides" && (
          <SectionCard title={`Guides — ${GUIDES.length} articles`}>
            <div className="space-y-2">
              {GUIDES.map((g) => (
                <GuideCard key={g.title} guide={g} />
              ))}
            </div>
          </SectionCard>
        )}

        {/* FAQ */}
        {tab === "faq" && (
          <SectionCard title={`Frequently Asked Questions — ${FAQS.length} entries`}>
            <div>
              {FAQS.map((item) => (
                <FaqRow key={item.question} item={item} />
              ))}
            </div>
          </SectionCard>
        )}

      </div>
    </div>
  )
}
